const leafField = (field) => String(field).split(".").pop();
const fixedAmount = (value) =>
  (Number(value) / 100).toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const duration = (value) => {
  const minutes = Math.round(Number(value));
  if (!Number.isFinite(minutes)) return String(value);
  return `${minutes < 0 ? "−" : ""}${Math.floor(Math.abs(minutes) / 60)} h ${
    Math.abs(minutes) % 60
  } min`;
};
const MINUTE_FIELDS = new Set([
  "durationMinutes",
  "adminMinutes",
  "minutes",
  "proposedMinutes",
  "approvedMinutes",
  "settledMinutes",
  "reportedMinutesOriginal",
]);

// Stored salary amounts are hundredths, not major currency units. Read the
// currency alongside an individual salary item; never assume it is DKK.
// Missing historical values remain different from explicit empty/false/zero.
export const describeValue = (value, field = "", context = {}) => {
  if (value === undefined) return "Not recorded";
  if (value === null) return "None";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  const leaf = leafField(field);
  const unit = context.units?.[field] || context.units?.[leaf];
  const numeric =
    ["number", "string"].includes(typeof value) &&
    value !== "" &&
    Number.isFinite(Number(value));
  if (numeric) {
    if (leaf === "premieOre" || unit === "DKK minor units per hour")
      return `${fixedAmount(value)} DKK/hour`;
    if (
      ["forudbetalingOre", "rejseomkOre"].includes(leaf) ||
      unit === "DKK minor units"
    )
      return `${fixedAmount(value)} DKK`;
    if (leaf === "feriedageHundredths" || unit === "hundredths of a day")
      return `${fixedAmount(value)} days`;
    if (leaf === "amountMinor" || unit === "currency minor units")
      return `${fixedAmount(value)} ${
        context.currency || "(currency not recorded)"
      }`;
    if (MINUTE_FIELDS.has(leaf) || unit === "minutes") return duration(value);
    if (leaf === "fxRate")
      return `1 EUR = ${Number(value).toLocaleString("en-GB", {
        maximumFractionDigits: 8,
      })} DKK`;
  }
  if (
    leaf === "hours" ||
    /hours(Logged|Approved|Contested|Settled|Agreed|Suggested)?$/i.test(leaf)
  ) {
    return numeric ? duration(Number(value) * 60) : String(value);
  }
  if (Array.isArray(value))
    return value.length
      ? value
          .map((item) => {
            if (
              item &&
              typeof item === "object" &&
              ("start" in item || "end" in item)
            )
              return `${item.start || "No start"}–${item.end || "No end"}`;
            return describeValue(item, leaf, context);
          })
          .join(", ")
      : "None";
  if (typeof value === "object")
    return Object.entries(value)
      .map(
        ([key, item]) =>
          `${fieldLabel(key)}: ${describeValue(item, key, {
            ...context,
            currency: Object.prototype.hasOwnProperty.call(value, "currency")
              ? value.currency
              : context.currency,
          })}`
      )
      .join("; ");
  return String(value);
};

const FIELD_LABELS = {
  hours: "Total hours",
  durationMinutes: "Recorded duration",
  adminMinutes: "Admin hours",
  approvedMinutes: "Approved hours",
  settledMinutes: "Settled hours",
  reportedMinutesOriginal: "Employee-reported hours",
  minutes: "Hours",
  segments: "Work and breaks",
  work: "Work periods",
  breaks: "Breaks",
  isDayOff: "Day off (Free)",
  dayOff: "Day off (Free)",
  finishedAt: "Day finished at",
  status: "Status",
  approval: "Approval",
  approvedBy: "Approved by account",
  approvedAt: "Approved at",
  challengeReason: "Dispute reason",
  proposedMinutes: "Suggested hours",
  employeeResponse: "Employee response",
  effectiveMonth: "Payroll month",
  hoursLogged: "Logged hours",
  hoursApproved: "Approved hours",
  hoursContested: "Contested hours",
  hoursSettled: "Settled hours",
  hoursAgreed: "Agreed hours",
  hoursSuggested: "Suggested hours",
  premieOre: "Premium rate (DKK/hour)",
  forudbetalingOre: "Forudbetaling (advance adjustment)",
  rejseomkOre: "Travel adjustment",
  feriedageHundredths: "Holiday days",
  salaryItems: "Salary items",
  type: "Type",
  amountMinor: "Amount",
  currency: "Currency",
  taxable: "Taxable",
  placementId: "Placement ID",
  fxRate: "EUR/DKK exchange rate",
  fxSource: "Exchange-rate source",
  payrollEndedAt: "Payroll ended at",
  taxCardReceived: "Tax card received",
  payrollOpen: "Payroll open",
  note: "Note",
  projectId: "Project ID",
  description: "Description",
  date: "Workday",
  customerId: "Customer ID",
  employeeId: "Employee ID",
  challengeResolution: "Dispute resolution",
};
export const fieldLabel = (field) =>
  String(field)
    .split(".")
    .map(
      (part) =>
        FIELD_LABELS[part] ||
        part.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ")
    )
    .join(" · ");

export const personLabel = (person, fallback = "Not recorded") =>
  person?.name ||
  person?.email ||
  (person?.id != null ? `Account #${person.id}` : fallback);

export const formatLogTimestamp = (value) => {
  if (!value) return "Not recorded";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString(undefined, { timeZoneName: "short" });
};

export const buildLogParams = (filters) => {
  const result = {
    page: filters.page,
    limit: filters.limit,
    sortBy: "timestamp",
    sortOrder: filters.sortOrder,
  };
  for (const key of [
    "type",
    "level",
    "search",
    "action",
    "requestId",
    "workDate",
    "actorUserId",
    "staffAccountId",
    "employeeId",
    "customerId",
    "timeEntryId",
  ]) {
    const value = String(filters[key] || "").trim();
    if (value && value !== "all") result[key] = value;
  }
  // datetime-local describes the administrator's local timezone; send an unambiguous instant.
  for (const key of ["startDate", "endDate"])
    if (filters[key]) result[key] = new Date(filters[key]).toISOString();
  return result;
};
